import { Check, Inbox, MoveRight } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { mediaUrl } from "../lib/api.js"
import { getTelegramWebApp } from "../lib/telegram.js"
import { useStore, useT } from "../stores/StoreProvider.js"

export const InboxSection = observer(function InboxSection() {
  const { galleryStore, authStore } = useStore()
  const t = useT()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showGalleryPicker, setShowGalleryPicker] = useState(false)

  useEffect(() => {
    if (authStore.isAuthenticated) {
      void galleryStore.fetchInbox()
    }
  }, [authStore.isAuthenticated, galleryStore])

  const inboxState = galleryStore.inboxState
  if (inboxState.status !== "loaded" || inboxState.items.length === 0) return null

  const { items, retentionHours } = inboxState

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    getTelegramWebApp()?.HapticFeedback.selectionChanged()
  }

  const selectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map((m) => m.id)))
    }
    getTelegramWebApp()?.HapticFeedback.impactOccurred("light")
  }

  const handleMove = (targetGalleryId: string) => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    void galleryStore.moveMedia(ids, targetGalleryId).then((ok) => {
      if (ok) {
        setSelectedIds(new Set())
        setShowGalleryPicker(false)
        getTelegramWebApp()?.HapticFeedback.notificationOccurred("success")
      }
    })
  }

  return (
    <div className="px-4 pt-4 pb-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-tg-accent" />
          <h2 className="text-sm font-semibold text-tg-text">{t("inbox.title")}</h2>
          <span className="text-xs text-tg-hint bg-tg-secondary px-1.5 py-0.5 rounded-full">
            {items.length}
          </span>
        </div>
        <span className="text-[11px] text-tg-hint">
          {t("inbox.expires_hint").replace("{hours}", String(retentionHours))}
        </span>
      </div>

      {/* Photo strip */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => toggleSelect(item.id)}
            className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden btn-press ${
              selectedIds.has(item.id) ? "ring-2 ring-tg-accent" : ""
            }`}
          >
            <img
              src={mediaUrl(item.id)}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {selectedIds.has(item.id) && (
              <div className="absolute inset-0 bg-tg-accent/30 flex items-center justify-center">
                <div className="w-5 h-5 rounded-full bg-tg-accent flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={selectAll}
            className="text-xs text-tg-accent font-medium px-2 py-1 rounded-lg bg-tg-accent/10 btn-press"
          >
            {selectedIds.size === items.length ? t("common.done") : t("inbox.select_all")}
          </button>
          <button
            type="button"
            onClick={() => setShowGalleryPicker(true)}
            className="flex items-center gap-1 text-xs text-tg-button-text font-medium px-3 py-1 rounded-lg bg-tg-accent btn-press"
          >
            <MoveRight className="w-3 h-3" />
            {t("inbox.move_to")} ({selectedIds.size})
          </button>
        </div>
      )}

      {/* Gallery picker modal */}
      {showGalleryPicker && (
        <GalleryPickerModal
          onSelect={handleMove}
          onClose={() => setShowGalleryPicker(false)}
        />
      )}
    </div>
  )
})

const GalleryPickerModal = observer(function GalleryPickerModal({
  onSelect,
  onClose,
}: {
  onSelect: (galleryId: string) => void
  onClose: () => void
}) {
  const { galleryStore } = useStore()
  const t = useT()

  const galleries =
    galleryStore.listState.status === "loaded" ? galleryStore.listState.galleries : []

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div
        className="w-full max-w-md bg-tg-bg rounded-t-2xl p-4 pb-8 page-enter"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={() => {}}
      >
        <div className="w-10 h-1 bg-tg-hint/30 rounded-full mx-auto mb-4" />
        <h3 className="text-base font-semibold text-tg-text mb-3">{t("inbox.move_to")}</h3>
        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
          {galleries.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => onSelect(g.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl active:bg-tg-secondary/50 transition-colors text-left"
            >
              {g.coverMediaId ? (
                <img
                  src={mediaUrl(g.coverMediaId)}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-tg-secondary flex items-center justify-center">
                  <Inbox className="w-5 h-5 text-tg-hint/40" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-tg-text truncate">{g.title}</div>
                <div className="text-xs text-tg-hint">{g.mediaCount} {t("home.photos")}</div>
              </div>
            </button>
          ))}
          {galleries.length === 0 && (
            <p className="text-tg-hint text-sm text-center py-4">{t("home.no_galleries_yet")}</p>
          )}
        </div>
      </div>
    </div>
  )
})
